/**
 * Investor badges, DERIVED from facts rather than stored.
 *
 * WHAT WAS WRONG. `InvestorProfile.badges` was a client-only field with no
 * column, no table, no mapper and no writer. Its only source in the entire
 * codebase was a literal array in `mock-data.ts`, which asserted "50+
 * investments", "80% lead rounds", "94% response rate", "4.8/5 rating" and "8
 * board seats" about a demo investor. In production the field was always
 * `undefined`, so the whole strip silently vanished. Demo mode was advertising
 * credentials that did not exist and could never be earned.
 *
 * This is the same failure the `investor_founder_reviews` and
 * `get_investor_engagement_metrics` migrations were written to end, and the
 * same rule the profile screen already states out loud about presence dots:
 * a signal shown to founders is a promise, and we do not make one we cannot
 * keep.
 *
 * WHY DERIVED AND NOT STORED. A stored badge is a badge somebody can grant
 * themselves — every other editable field on this screen is self-asserted, and
 * "Founder Favorite" must not be. Deriving on read also means a badge cannot go
 * stale: it is recomputed from the same rows that justify it, so it disappears
 * the moment it stops being true. That costs nothing here, because the screen
 * has already loaded every input by the time it renders.
 *
 * THE THRESHOLDS ARE NOT MINE. `getInvestorBadgeInfo` in `components/Badge.tsx`
 * already prints "50+ deals", "<48h", "4.5+ rating" — the UI has been making
 * those specific promises all along. The rules below match them exactly. If a
 * threshold should move, the label has to move with it, in that file.
 *
 * WHAT IS DELIBERATELY ABSENT. Four of the ten badge types have no honest
 * source and are NOT emitted:
 *
 *   - `lead-investor`      The only lead signal in the schema is
 *                          `investor_profiles.lead_preference`, which is a
 *                          self-declared *intention*. `investment_type` is
 *                          participation structure, not who led. The mock's
 *                          "80% lead rounds" had nothing behind it whatsoever.
 *   - `first-check`        Nothing records whether a round was the company's
 *                          first. `stage: 'pre-seed'` is a different claim.
 *   - `follow-on-champion` `followOnRate` was explicitly dropped from the
 *                          engagement RPC as "not derivable from anything the
 *                          product records" — reviving it here would undo that.
 *   - `reliable-committer` `Badge` has no config for it and renders `null`, and
 *                          the metric behind it is computed over a device-local
 *                          zustand array with no server load.
 *
 * Adding any of them means adding the data first, not the badge.
 */

import type { InvestorBadge } from '@/lib/state/profile-store';
import type { InvestmentRecord } from '@/lib/state/profile-store';
import type { InvestorEngagementMetrics } from '@/lib/investor/profile-signals';
import type { InvestorReview } from '@/lib/investor/reviews';
import { averageRating } from '@/lib/investor/reviews';

/**
 * Everything the derivation is allowed to look at.
 *
 * Every field is optional because an absent signal must produce an absent
 * badge, never a defaulted one. A signal that has not loaded yet and a signal
 * that is genuinely zero both correctly yield no badge.
 */
export interface InvestorBadgeSignals {
  /** `investor_profiles.linkedin_verified` — the profile was confirmed to exist. */
  linkedInVerified?: boolean;
  /** `investor_profiles.linkedin_verified_at` — the one real `earnedAt` available. */
  linkedInVerifiedAt?: string;
  /** Rows from `investor_track_record`. NOT the stale `portfolio_count` aggregate. */
  trackRecord?: InvestmentRecord[];
  /** From `get_investor_engagement_metrics`. */
  engagement?: InvestorEngagementMetrics;
  /** From `get_investor_reviews`. */
  reviews?: InvestorReview[];
}

/**
 * MEASURED AGAINST THE LABELS, not chosen freely. Each constant below is the
 * number `getInvestorBadgeInfo` already shows to founders.
 */
const SERIAL_INVESTOR_MIN_DEALS = 50; // label: "50+ deals"
const QUICK_RESPONDER_MAX_HOURS = 48; // label: "<48h"
const FOUNDER_FAVORITE_MIN_RATING = 4.5; // label: "4.5+ rating"

/**
 * Floors that stop a badge being earned by a sample of one.
 *
 * The engagement RPC deliberately exposes `eligibleThreadCount` so that "100%
 * response rate" off a single thread reads honestly; a badge has to respect the
 * same caveat or it launders one data point into a credential.
 */
const QUICK_RESPONDER_MIN_THREADS = 3;
const FOUNDER_FAVORITE_MIN_REVIEWS = 3;

/** A specialism means concentration, not merely having invested once. */
const SECTOR_SPECIALIST_MIN_DEALS = 3;
const SECTOR_SPECIALIST_MIN_SHARE = 0.5;

/** Governance roles that constitute board experience, per the track-record enum. */
const BOARD_ROLES: ReadonlyArray<InvestmentRecord['investorRole']> = [
  'board-member',
  'board-observer',
];

/**
 * Derive the badges an investor has actually earned.
 *
 * Order is stable and meaningful: identity first, then track record, then
 * behaviour towards founders. The profile screen shows the first six.
 */
export function deriveInvestorBadges(signals: InvestorBadgeSignals): InvestorBadge[] {
  const badges: InvestorBadge[] = [];
  const trackRecord = signals.trackRecord ?? [];
  const reviews = signals.reviews ?? [];

  if (signals.linkedInVerified) {
    badges.push({
      type: 'linkedin-verified',
      // The only badge with a real earned date, because it is the only one
      // backed by a stored timestamp rather than recomputed from rows.
      earnedAt: signals.linkedInVerifiedAt,
    });
  }

  if (trackRecord.length >= SERIAL_INVESTOR_MIN_DEALS) {
    badges.push({ type: 'serial-investor', detail: `${trackRecord.length} investments` });
  }

  const boardSeats = trackRecord.filter((r) => BOARD_ROLES.includes(r.investorRole)).length;
  if (boardSeats > 0) {
    // Counted per company from the track record rather than read from the
    // self-entered `board_seats` integer: both are self-reported, but this one
    // names the companies it is counting.
    badges.push({
      type: 'board-expert',
      detail: `${boardSeats} board seat${boardSeats === 1 ? '' : 's'}`,
    });
  }

  const sector = dominantSector(trackRecord);
  if (sector) {
    // `detail` is the ONE place the Badge component actually reads it — it
    // replaces the label for this type.
    badges.push({ type: 'sector-specialist', detail: sector });
  }

  const { medianResponseHours, eligibleThreadCount } = signals.engagement ?? {};
  if (
    medianResponseHours !== null &&
    medianResponseHours !== undefined &&
    medianResponseHours < QUICK_RESPONDER_MAX_HOURS &&
    (eligibleThreadCount ?? 0) >= QUICK_RESPONDER_MIN_THREADS
  ) {
    badges.push({ type: 'quick-responder', detail: `${medianResponseHours}h median reply` });
  }

  const rating = averageRating(reviews);
  if (
    rating !== null &&
    rating >= FOUNDER_FAVORITE_MIN_RATING &&
    reviews.length >= FOUNDER_FAVORITE_MIN_REVIEWS
  ) {
    badges.push({
      type: 'founder-favorite',
      detail: `${rating.toFixed(1)}/5 from ${reviews.length} founders`,
    });
  }

  return badges;
}

/**
 * The sector an investor demonstrably concentrates in, or null.
 *
 * `industry` is nullable free text on each record, so entries without one are
 * excluded from BOTH the numerator and the denominator — an investor who filled
 * in one row out of forty is not thereby a specialist in it.
 */
function dominantSector(trackRecord: InvestmentRecord[]): string | null {
  const withIndustry = trackRecord.filter(
    (r): r is InvestmentRecord & { industry: string } => !!r.industry?.trim(),
  );
  if (withIndustry.length < SECTOR_SPECIALIST_MIN_DEALS) return null;

  const counts = new Map<string, { label: string; n: number }>();
  for (const record of withIndustry) {
    const label = record.industry.trim();
    const key = label.toLowerCase();
    const entry = counts.get(key);
    if (entry) entry.n += 1;
    else counts.set(key, { label, n: 1 });
  }

  let top: { label: string; n: number } | null = null;
  for (const entry of counts.values()) {
    if (!top || entry.n > top.n) top = entry;
  }
  if (!top) return null;

  const share = top.n / withIndustry.length;
  if (top.n < SECTOR_SPECIALIST_MIN_DEALS || share < SECTOR_SPECIALIST_MIN_SHARE) return null;
  return top.label;
}
